import { Button } from '@creditornot/cb-button';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportEvent } from 'modules/campaigns/behavioral';
import { AdCampaignScheduleFormValues, CampaignStatus } from 'modules/campaigns/types';

import { AdCampaignScheduleDetails } from './AdCampaignScheduleDetails';
import { AdCampaignScheduleFormModal } from './AdCampaignSchedulingModal/AdCampaignScheduleFormModal';
import { schedulesValidationSchema } from './adCampaignScheduleValidationSchema';
import UnsavedChangesModal from '../../modals/UnsavedChangesModal/UnsavedChangesModal';

export type AdCampaignScheduleSectionItemProps = {
  initialValues: AdCampaignScheduleFormValues;
  campaignStatus?: CampaignStatus;
  campaignId?: string;
  hasCampaignStarted?: boolean;
  onUpdate: (formData: AdCampaignScheduleFormValues) => void;
};

export const AdCampaignScheduleSectionItem = ({
  initialValues,
  campaignStatus,
  campaignId,
  hasCampaignStarted,
  onUpdate,
}: AdCampaignScheduleSectionItemProps) => {
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsScheduleModalOpen(false);
    }
  };

  const handleSubmitForm = (formData: AdCampaignScheduleFormValues) => {
    onUpdate({
      startDate: formData.startDate,
      endDate: formData.scheduleStrategy === 'selectDates' ? formData.endDate : undefined,
      scheduleStrategy: formData.endDate ? 'selectDates' : 'alwaysOn',
    });

    handleCloseModals();
  };

  const handleValidate = (values: AdCampaignScheduleFormValues) => {
    return validateYupSchema(schedulesValidationSchema)(values);
  };

  const handleCampaignScheduleCtaClick = () => {
    setIsScheduleModalOpen(true);
    reportEvent('NEW_AD_CAMPAIGN_EDIT_SCHEDULE_BUTTON');
  };

  return (
    <>
      <Form<AdCampaignScheduleFormValues>
        initialValues={{
          ...initialValues,
          scheduleStrategy: initialValues.endDate ? 'selectDates' : 'alwaysOn',
          startDate: initialValues.startDate ?? DateTime.local().toISO(),
        }}
        onSubmit={handleSubmitForm}
        validate={values => handleValidate(values)}
      >
        {({ handleSubmit, values, errors, form, dirty }) => (
          <WoltModal isOpen={isScheduleModalOpen} onRequestClose={() => handleCloseModals(dirty)}>
            <AdCampaignScheduleFormModal
              scheduleStrategy={values.scheduleStrategy}
              errors={errors}
              onSubmit={handleSubmit}
              onCloseClick={() => handleCloseModals(dirty)}
              isDisabled={
                campaignStatus === 'running' || campaignStatus === 'stopped' || campaignStatus === 'completed'
              }
              isDirty={dirty}
            />
            <UnsavedChangesModal
              isOpen={dirty && isUnsavedChangesModalOpen}
              onDropChanges={() => {
                handleCloseModals();
                form.reset();
              }}
              onClose={() => setIsUnsavedChangesModalOpen(false)}
            />
          </WoltModal>
        )}
      </Form>
      <AdCampaignScheduleDetails
        campaignId={campaignId}
        startDate={initialValues.startDate ?? DateTime.local().toISO()}
        endDate={initialValues.endDate}
        scheduleStrategy={initialValues.endDate ? 'selectDates' : 'alwaysOn'}
        action={
          <Button
            variant="lightBlue"
            size="small"
            disabled={hasCampaignStarted}
            onClick={handleCampaignScheduleCtaClick}
          >
            <FormattedMessage id="campaigns.form.scheduling.edit-schedule" />
          </Button>
        }
      />
    </>
  );
};
